import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface OpcionBusqueda {
  id: number;
  texto: string;
  /** Texto secundario (p. ej. el tipo de asociación). */
  detalle?: string;
  /** Valor con el que filtra el selector de grupo (p. ej. el tipo de asociación o 0/1 adulto/infantil). */
  grupo?: number | null;
  /** Se muestra con la etiqueta «(inactiva)» pero sigue siendo seleccionable (el histórico es histórico). */
  inactiva?: boolean;
}

export interface GrupoBusqueda { id: number; texto: string; }

const MAXIMO_VISIBLE = 40;

/** Texto comparable: sin acentos ni mayúsculas. */
export function normalizarTexto(valor: unknown): string {
  return String(valor ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Selector de catálogo con búsqueda: pensado para listas de decenas o cientos de elementos (asociaciones, cargos). Filtra en el
 * navegador por texto (sin acentos), ordena alfabéticamente, admite un filtro por grupo (tipo) y no muestra más de 40 opciones a la vez.
 * La elección queda como ficha con botón «Cambiar». No pide datos: las opciones las aporta el padre.
 */
@Component({
  selector: 'app-selector-busqueda',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './selector-busqueda.component.html'
})
export class SelectorBusquedaComponent {
  @Input({ required: true }) opciones: OpcionBusqueda[] = [];
  @Input() etiqueta = 'Elemento';
  @Input() placeholder = 'Escribe para buscar';
  @Input() inputId = 'selector-busqueda';
  @Input() cargando = false;
  @Input() error = '';
  /** Filtro opcional por grupo (tipo de asociación, adulto/infantil…). */
  @Input() grupos: GrupoBusqueda[] = [];
  @Input() etiquetaGrupo = 'Tipo';
  @Input() valor: number | null = null;
  @Input() deshabilitado = false;

  @Output() elegido = new EventEmitter<number | null>();
  @Output() reintentar = new EventEmitter<void>();

  texto = '';
  grupo: number | '' = '';
  abierto = false;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get seleccionada(): OpcionBusqueda | null {
    return this.valor === null ? null : this.opciones.find(o => o.id === this.valor) ?? null;
  }

  get filtradas(): OpcionBusqueda[] {
    const buscado = normalizarTexto(this.texto);
    return this.opciones
      .filter(o => (this.grupo === '' || o.grupo === this.grupo) && (!buscado || normalizarTexto(o.texto).includes(buscado)))
      .sort((a, b) => a.texto.localeCompare(b.texto, 'es'));
  }

  get visibles(): OpcionBusqueda[] { return this.filtradas.slice(0, MAXIMO_VISIBLE); }
  get ocultas(): number { return Math.max(0, this.filtradas.length - MAXIMO_VISIBLE); }

  abrir(): void { if (!this.deshabilitado) this.abierto = true; }
  cerrar(): void { this.abierto = false; }

  elegir(opcion: OpcionBusqueda): void {
    this.valor = opcion.id;
    this.texto = '';
    this.abierto = false;
    this.elegido.emit(opcion.id);
  }

  quitar(): void {
    this.valor = null;
    this.elegido.emit(null);
  }

  /** Enter elige la primera opción cuando solo queda una coincidencia exacta o única. */
  intro(): void {
    const lista = this.filtradas;
    if (lista.length === 1) this.elegir(lista[0]);
  }

  @HostListener('document:click', ['$event'])
  fueraDelSelector(evento: Event): void {
    if (this.abierto && !this.host.nativeElement.contains(evento.target as Node)) this.cerrar();
  }
}
